import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { CamposForm, GRADE_FORM, useEstadoForm } from './form';
import { Historico } from './Historico';
import type { PropsCriarCampo } from '../../nucleo/modulos-web';
import { sensivelDoFormulario, TIPOS_SEMPRE_SENSIVEIS, type Campo, type TipoCampo } from './tipos';

const TIPOS: TipoCampo[] = ['texto', 'texto_longo', 'numero', 'dinheiro', 'data', 'lista', 'booleano', 'telefone', 'email', 'documento', 'cep'];

/**
 * Campos fixos que a condição "mostrar quando" aceita, por entidade (fase 2, W5), com os valores; a mesma lista do servidor
 * (`CAMPOS_FIXOS_CONDICAO`). Além deles, qualquer outro campo personalizado da entidade.
 */
const FIXOS_CONDICAO: Record<string, Record<string, string[]>> = {
  'cadastros.parceiro': { tipo: ['pessoa', 'empresa'], e_cliente: ['true', 'false'], e_fornecedor: ['true', 'false'], e_parceiro: ['true', 'false'] },
};
const chaveI18n = (entidade: string) => entidade.replace('.', '_');

/**
 * Lista de 03/11, item 7: Empresas e Contatos são a mesma entidade (`cadastros.parceiro`); cada aba fixa a condição `tipo`.
 * `aba` é o rótulo da aba (já traduzido) para os avisos.
 */
export interface CondicaoDaAba {
  campo: string;
  valor: string;
  aba: string;
}
const daAba = (c: Campo | null, aba?: CondicaoDaAba) => !!aba && !!c?.visivel_quando && c.visivel_quando.campo === aba.campo && c.visivel_quando.valor === aba.valor;
/** O campo aparece na aba? Só some quando a condição é o mesmo campo da aba com outro valor (a outra aba); sem condição, aparece nas duas. */
export const campoNaAba = (c: Campo, aba?: CondicaoDaAba) => !aba || !c.visivel_quando || c.visivel_quando.campo !== aba.campo || c.visivel_quando.valor === aba.valor;

function FormCampo({ entidade, campo, outros, aoSalvar, ordemInicial = 0, abaFixa }: { entidade: string; campo: Campo | null; outros: Campo[]; aoSalvar: () => Promise<unknown>; ordemInicial?: number; abaFixa?: CondicaoDaAba }) {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const editando = !!campo;
  // Val5 (29/10): campos personalizados têm ação própria (`configuracoes.campos`).
  const bloqueado = !podeNoWorkspace(s, 'configuracoes.campos');
  const f = useEstadoForm<Record<string, unknown>>({
    chave: campo?.chave ?? '',
    rotulo: campo?.rotulo ?? '',
    tipo: campo?.tipo ?? 'texto',
    opcoes: (campo?.opcoes ?? []).join('\n'),
    obrigatorio: campo?.obrigatorio ?? false,
    ordem: campo?.ordem ?? ordemInicial,
    sensivel: campo?.sensivel ?? false,
    ajuda: campo?.ajuda ?? '',
    cond_campo: campo?.visivel_quando?.campo ?? '',
    cond_valor: campo?.visivel_quando?.valor ?? '',
    so_aba: false,
  });
  // Campo novo: a caixa segue a regra por nome até o administrador tocar nela.
  const [tocado, setTocado] = useState(editando);
  const sempre = TIPOS_SEMPRE_SENSIVEIS.includes(f.valores.tipo as TipoCampo);
  // "Mostrar quando" (fase 2, W5): outro campo personalizado da entidade ou um campo fixo dela.
  // Em uma aba com condição fixa (item 7 de 03/11): campo novo nasce com a condição da aba; campo da aba segue com ela; campo
  // sem condição ganha a caixa "Só nesta aba"; campo com outra condição mantém o seletor, sem o campo da aba (coerente).
  const condicaoDaAba = !!abaFixa && (!campo || daAba(campo, abaFixa));
  const restringivel = !!abaFixa && !!campo && !campo.visivel_quando;
  const fixosTodos = FIXOS_CONDICAO[entidade] ?? {};
  const fixos = abaFixa ? Object.fromEntries(Object.entries(fixosTodos).filter(([k]) => k !== abaFixa.campo)) : fixosTodos;
  const opcoesCondicao = [
    ...Object.keys(fixos).map((k) => ({ valor: k, rotulo: t(`campos.condicaoFixos.${k}`, { defaultValue: k }) })),
    ...outros.filter((o) => o.id !== campo?.id).map((o) => ({ valor: o.chave, rotulo: o.rotulo })),
  ];
  const valoresDaCondicao = (c: unknown): { valor: string; rotulo: string }[] | null => {
    const k = String(c ?? '');
    if (fixos[k]) return fixos[k]!.map((x) => ({ valor: x, rotulo: t(`campos.condicaoValores.${x}`, { defaultValue: x }) }));
    const o = outros.find((x) => x.chave === k);
    if (o?.tipo === 'lista') return o.opcoes.map((x) => ({ valor: x, rotulo: x }));
    if (o?.tipo === 'booleano') return ['true', 'false'].map((x) => ({ valor: x, rotulo: t(`campos.condicaoValores.${x}`) }));
    return null;
  };
  const valores = { ...f.valores, sensivel: sensivelDoFormulario(f.valores, !tocado) };
  const defs: DefCampo[] = [
    { nome: 'rotulo', rotulo: t('campos.rotulo'), tipo: 'texto', obrigatorio: true, desabilitado: bloqueado },
    { nome: 'chave', rotulo: t('campos.chave'), tipo: 'texto', obrigatorio: true, largura: 'meia', ajuda: editando ? undefined : t('campos.chaveDica'), desabilitado: editando || bloqueado },
    { nome: 'tipo', rotulo: t('campos.tipo'), tipo: 'selecao', obrigatorio: true, largura: 'meia', desabilitado: editando || bloqueado, opcoes: TIPOS.map((x) => ({ valor: x, rotulo: t(`campos.tipos.${x}`) })) },
    { nome: 'opcoes', rotulo: t('campos.opcoes'), tipo: 'area', ajuda: t('campos.opcoesDica'), visivel: (v) => v.tipo === 'lista', desabilitado: bloqueado },
    { nome: 'ordem', rotulo: t('campos.ordem'), tipo: 'numero', min: 0, max: 10000, passo: 1, largura: 'meia', desabilitado: bloqueado },
    { nome: 'obrigatorio', rotulo: t('campos.obrigatorio'), tipo: 'booleano', largura: 'meia', desabilitado: bloqueado },
    { nome: 'ajuda', rotulo: t('campos.ajudaCampo'), tipo: 'texto', ajuda: t('campos.ajudaCampoDica'), desabilitado: bloqueado },
    ...(restringivel
      ? ([{ nome: 'so_aba', rotulo: t('campos.soNestaAba', { aba: abaFixa!.aba }), tipo: 'booleano', ajuda: t('campos.soNestaAbaDica'), desabilitado: bloqueado }] satisfies DefCampo[])
      : []),
    ...(condicaoDaAba || restringivel
      ? []
      : ([{ nome: 'cond_campo', rotulo: t('campos.condicao'), tipo: 'selecao', largura: 'meia', opcoes: opcoesCondicao, ajuda: t('campos.condicaoDica'), desabilitado: bloqueado }] satisfies DefCampo[])),
    ...(condicaoDaAba || restringivel ? [] : valoresDaCondicao(f.valores.cond_campo)
      ? ([{ nome: 'cond_valor', rotulo: t('campos.condicaoValor'), tipo: 'selecao', largura: 'meia', opcoes: valoresDaCondicao(f.valores.cond_campo)!, visivel: (v) => !!v.cond_campo, desabilitado: bloqueado }] satisfies DefCampo[])
      : ([{ nome: 'cond_valor', rotulo: t('campos.condicaoValor'), tipo: 'texto', largura: 'meia', visivel: (v) => !!v.cond_campo, desabilitado: bloqueado }] satisfies DefCampo[])),
    {
      nome: 'sensivel',
      rotulo: t('campos.sensivel'),
      tipo: 'booleano',
      ajuda: sempre ? t('campos.sensivelSempre') : t('campos.sensivelDica'),
      visivel: (v) => v.tipo !== 'booleano',
      desabilitado: bloqueado || sempre,
    },
  ];

  return (
    <form
      id="form-campo"
      noValidate
      className={GRADE_FORM}
      onSubmit={(ev) =>
        f.enviar(ev, defs, tn('comum.obrigatorio'), async (bruto) => {
          const v: Record<string, unknown> = { ...bruto, sensivel: sensivelDoFormulario(bruto, !tocado) };
          const opcoes = String(v.opcoes ?? '')
            .split('\n')
            .map((o) => o.trim())
            .filter(Boolean);
          const condicao =
            abaFixa && (!campo || v.so_aba) ? { campo: abaFixa.campo, valor: abaFixa.valor } : v.cond_campo && String(v.cond_valor ?? '').trim() ? { campo: String(v.cond_campo), valor: String(v.cond_valor).trim() } : null;
          const comum = {
            rotulo: v.rotulo,
            obrigatorio: !!v.obrigatorio,
            ordem: Number(v.ordem) || 0,
            sensivel: v.sensivel,
            ajuda: String(v.ajuda ?? '').trim() || null,
            visivel_quando: condicao,
            ...(v.tipo === 'lista' ? { opcoes } : {}),
          };
          if (campo) await patch(`/configuracoes/campos/${campo.id}`, comum);
          else await post('/configuracoes/campos', { ...comum, entidade, chave: String(v.chave ?? '').trim(), tipo: v.tipo });
          await aoSalvar();
        })
      }
    >
      <p className="text-sm text-tinta-3 sm:col-span-6">{t(`campos.entidades.${chaveI18n(entidade)}`)}</p>
      {abaFixa && condicaoDaAba && (
        <p className="text-sm text-tinta-2 sm:col-span-6" data-campo-da-aba="">
          {t('campos.soNaAba', { aba: abaFixa.aba })}
        </p>
      )}
      <CamposForm
        defs={defs}
        valores={valores}
        erros={f.erros}
        erroGeral={f.erroGeral}
        aoMudar={(nome, v) => {
          if (nome === 'sensivel') setTocado(true);
          f.definir(nome, v);
        }}
      />
      {editando && <p className="text-xs text-tinta-3 sm:col-span-6">{t('campos.tipoFixo')}</p>}
    </form>
  );
}

/**
 * M13.3: campos personalizados por entidade dos módulos instalados.
 * Lote crm7: as Configurações do CRM mostram a lista de uma entidade só (`entidadeFixa`, escolhida pelas abas da seção, sem o
 * seletor nem o histórico) e põem o "Criar campo" no próprio cabeçalho (`cabecalho`, que recebe o botão).
 */
export function Campos({ entidadeFixa, cabecalho, condicaoAba }: { entidadeFixa?: string; cabecalho?: (novo: ReactNode) => ReactNode; condicaoAba?: CondicaoDaAba } = {}) {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const podeEditar = podeNoWorkspace(s, 'configuracoes.campos');
  const entidades = useQuery({ queryKey: ['configuracoes', 'campos', 'entidades'], queryFn: () => get<{ entidade: string; modulo: string }[]>('/configuracoes/campos/entidades') });
  const [escolhida, setEntidade] = useState('');
  const entidade = entidadeFixa ?? escolhida;
  // crm5 (E2): "Adicionar campo personalizado" do formulário da empresa do CRM chega com `?entidade=cadastros.parceiro`.
  const [busca] = useSearchParams();
  const pedida = busca.get('entidade');
  useEffect(() => {
    if (entidadeFixa || entidade || !entidades.data?.length) return;
    setEntidade(entidades.data.find((e) => e.entidade === pedida)?.entidade ?? entidades.data[0]!.entidade);
  }, [entidadeFixa, entidade, entidades.data, pedida]);
  const q = useQuery({
    queryKey: ['configuracoes', 'campos', entidade],
    queryFn: () => get<{ itens: Campo[] }>('/configuracoes/campos', { entidade }),
    enabled: !!entidade,
  });
  const [aberto, setAberto] = useState<string | 'novo' | null>(null);
  const [desativando, setDesativando] = useState<Campo | null>(null);
  const atual = aberto && aberto !== 'novo' ? (q.data?.itens.find((c) => c.id === aberto) ?? null) : null;

  const recarregar = () => Promise.all([qc.invalidateQueries({ queryKey: ['configuracoes', 'campos'] }), qc.invalidateQueries({ queryKey: ['campos'] }), qc.invalidateQueries({ queryKey: ['configuracoes', 'historico'] })]);
  const desativar = useMutation({
    mutationFn: (id: string) => del(`/configuracoes/campos/${id}`),
    onSuccess: async () => {
      setDesativando(null);
      await recarregar();
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => {
      setDesativando(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  const novo = podeEditar && !!entidade && (
    <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')} data-novo-campo="">
      {t('acoes.novoCampo')}
    </Botao>
  );
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {cabecalho ? cabecalho(novo || null) : novo && <AcaoPrimaria>{novo}</AcaoPrimaria>}
      <Cartao titulo={t('campos.titulo')} semPadding>
        <Estado consulta={entidades}>
          {(es) =>
            es.length === 0 ? (
              <Vazio texto={t('campos.semEntidades')} />
            ) : (
              <>
                <div className="flex flex-wrap items-end gap-4 border-b border-borda px-4 py-3">
                  {!entidadeFixa && (
                  <div className="w-full sm:w-80">
                    <Rotulo id="campos-entidade" rotulo={t('campos.entidade')}>
                      <Selecao id="campos-entidade" opcoes={es.map((e) => ({ valor: e.entidade, rotulo: t(`campos.entidades.${chaveI18n(e.entidade)}`) }))} value={entidade} onChange={(ev) => setEntidade(ev.target.value)} />
                    </Rotulo>
                  </div>
                  )}
                  <p className="min-w-0 flex-1 basis-60 text-sm text-tinta-3">{t('campos.ajuda')}</p>
                </div>
                <Estado consulta={q}>
                  {(d) =>
                    d.itens.filter((c) => campoNaAba(c, condicaoAba)).length === 0 ? (
                      <Vazio texto={t('campos.vazio')} />
                    ) : (
                      <ul className="divide-y divide-borda">
                        {d.itens.filter((c) => campoNaAba(c, condicaoAba)).map((c) => (
                          <li key={c.id} className="flex min-w-0 items-center gap-2 pr-3 hover:bg-sup-2">
                            <button type="button" onClick={() => setAberto(c.id)} className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left">
                              <span className="flex min-w-0 flex-1 basis-44 flex-col">
                                <span className="truncate font-medium" title={c.rotulo}>{c.rotulo}</span>
                                <span className="numero truncate text-xs text-tinta-3" title={c.chave}>{c.chave}</span>
                              </span>
                              <Selo cor="azul">{t(`campos.tipos.${c.tipo}`)}</Selo>
                              {c.obrigatorio && <Selo cor="laranja">{t('campos.obrigatorio')}</Selo>}
                              {c.sensivel && <Selo cor="roxo">{t('campos.sensivelSelo')}</Selo>}
                              {c.visivel_quando && !daAba(c, condicaoAba) && <Selo>{t('campos.condicaoSelo')}</Selo>}
                              {condicaoAba && !c.visivel_quando && <Selo>{t('campos.nasDuasAbas')}</Selo>}
                              <span className="numero w-8 text-right text-xs text-tinta-3" title={t('campos.ordem')}>
                                {c.ordem}
                              </span>
                            </button>
                            {podeEditar && (
                              <button type="button" aria-label={`${t('acoes.desativar')}: ${c.rotulo}`} onClick={() => setDesativando(c)} className="rounded p-1.5 text-tinta-3 hover:text-perigo">
                                <Trash2 className="size-4" />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )
                  }
                </Estado>
              </>
            )
          }
        </Estado>
      </Cartao>

      {!entidadeFixa && <Historico entidade="configuracoes.campo" titulo={t('campos.historico')} />}

      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        titulo={atual ? t('campos.editarTitulo') : t('campos.novoTitulo')}
        rodape={
          podeEditar && (
            <>
              <Botao variante="fantasma" onClick={() => setAberto(null)}>
                {tn('comum.cancelar')}
              </Botao>
              <Botao variante="primario" type="submit" form="form-campo">
                {tn('comum.salvar')}
              </Botao>
            </>
          )
        }
      >
        <FormCampo
          key={atual?.id ?? 'novo'}
          entidade={atual?.entidade ?? entidade}
          campo={atual}
          outros={q.data?.itens ?? []}
          abaFixa={condicaoAba}
          aoSalvar={async () => {
            await recarregar();
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        />
      </Drawer>

      <Confirmar
        aberto={!!desativando}
        aoFechar={() => setDesativando(null)}
        aoConfirmar={() => desativando && desativar.mutate(desativando.id)}
        carregando={desativar.isPending}
        perigoso
        titulo={t('campos.confirmarDesativar')}
        texto={t('campos.confirmarDesativarTexto')}
      />
    </div>
  );
}

/**
 * Lista de 30/10 (B), item 24: o pop-up de criar um campo personalizado de uma entidade, aberto de dentro de um cadastro (o
 * "Criar novos campos" do formulário). É o mesmo formulário da página de campos; o campo novo nasce depois dos que já existem
 * (a ordem segue o maior número da entidade) e entra no fim do cadastro, que se atualiza sem sair da tela. Publicado no módulo
 * (`criarCampo`) para os outros módulos abrirem sem importar esta tela.
 */
export function CriarCampo({ entidade, abaFixa, aberto, aoFechar, aoCriar }: PropsCriarCampo) {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const podeEditar = podeNoWorkspace(s, 'configuracoes.campos');
  const q = useQuery({ queryKey: ['configuracoes', 'campos', entidade], queryFn: () => get<{ itens: Campo[] }>('/configuracoes/campos', { entidade }), enabled: aberto && podeEditar });
  if (!podeEditar) return null;
  const maior = Math.max(0, ...(q.data?.itens ?? []).map((c) => c.ordem));
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('campos.novoTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-campo" disabled={!q.isSuccess}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <Estado consulta={q}>
        {(d) => (
          <FormCampo
            key={`${entidade}-novo`}
            entidade={entidade}
            campo={null}
            outros={d.itens}
            abaFixa={abaFixa}
            ordemInicial={d.itens.length ? maior + 10 : 0}
            aoSalvar={async () => {
              await Promise.all([qc.invalidateQueries({ queryKey: ['configuracoes', 'campos'] }), qc.invalidateQueries({ queryKey: ['campos'] }), qc.invalidateQueries({ queryKey: ['configuracoes', 'historico'] })]);
              toast.sucesso(tn('comum.salvo'));
              aoFechar();
              aoCriar?.();
            }}
          />
        )}
      </Estado>
    </Modal>
  );
}
